import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppNotify } from '../../../utilities/notification-helper';
import { API_URL } from '../../../services/endpoints';

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrl: './file-upload.component.scss',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadComponent  {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropZoneExternal') dropZoneExternal?: ElementRef<HTMLDivElement>;
  @Output() readonly onFileChanged = new EventEmitter<File>();

  @Input() allowedFileExtensions: string[] = ['.pdf'];
  @Input() maxFileSize: number = 5 * 1024 * 1024;

  uploadedFileSize?: string;
  uploadedFile?: File;
  isDropZoneActive = false;
  dropZoneValidationState: 'default' | 'success' | 'error' = 'default';
  imageSource = '';
  textVisible = true;
  progressVisible = false;
  progressValue = 0;

  private dragCounter = 0;
  private vlmWarmupFired = false;

  constructor(private cdr: ChangeDetectorRef, private http: HttpClient) {}

  private warmupVlm(): void {
    if (this.vlmWarmupFired) return;
    this.vlmWarmupFired = true;
    this.http.get(`${API_URL}/api/statement/warmup-vlm`).subscribe({ error: () => {} });
  }

  convertFileSize(bytes: number): string {
    const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    if (bytes === 0) return '0 Byte';

    const i: string = String(
      parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))))
    );

    return `${(
      Math.round(100 * (bytes / Math.pow(1024, parseInt(i)))) / 100
    ).toFixed(2)} ${sizes[parseInt(i)]}`;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;

    const file = files[0];

    // Check file extension
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!this.allowedFileExtensions.includes(fileExtension)) {
      this.showValidationError(
        `Invalid file type. Only ${this.allowedFileExtensions.join(', ')} files are allowed.`
      );
      this.resetUpload();
      return;
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      this.showValidationError(
        `File size exceeds the maximum allowed size of ${this.convertFileSize(this.maxFileSize)}.`
      );
      this.resetUpload();
      return;
    }

    this.processFile(file);
  }

  private processFile(file: File) {
    // Valid file — simulate upload progress
    this.uploadedFile = file;
    this.progressVisible = true;
    this.progressValue = 0;
    this.dropZoneValidationState = 'success';
    this.cdr.markForCheck();

    // Simulate progress
    const progressInterval = setInterval(() => {
      this.progressValue += 20;
      this.cdr.markForCheck();
      if (this.progressValue >= 100) {
        clearInterval(progressInterval);
        setTimeout(() => {
          this.uploadedFileSize = this.convertFileSize(file.size);
          this.onFileChanged.emit(file);
          this.textVisible = false;
          this.progressVisible = false;
          this.progressValue = 0;
          this.dropZoneValidationState = 'default';
          this.cdr.markForCheck();
        }, 300);
      }
    }, 100);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDropZoneClick(): void {
    this.warmupVlm();
    this.fileInput?.nativeElement.click();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.warmupVlm();
    this.dragCounter++;

    const items = event.dataTransfer?.items;
    if (items && items.length === 1) {
      const mimeType = items[0].type;
      // PDF MIME type is application/pdf; map to .pdf extension
      const ext = mimeType === 'application/pdf' ? '.pdf' : `.${mimeType.split('/').pop()}`;
      if (this.allowedFileExtensions.includes(ext)) {
        this.isDropZoneActive = true;
        this.cdr.markForCheck();
      }
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDropZoneActive = false;
      this.cdr.markForCheck();
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDropZoneActive = false;
    this.cdr.markForCheck();

    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    const file = files[0];

    // Check file extension
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!this.allowedFileExtensions.includes(fileExtension)) {
      this.showValidationError(
        `Invalid file type. Only ${this.allowedFileExtensions.join(', ')} files are allowed.`
      );
      this.resetUpload();
      return;
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      this.showValidationError(
        `File size exceeds the maximum allowed size of ${this.convertFileSize(this.maxFileSize)}.`
      );
      this.resetUpload();
      return;
    }

    this.processFile(file);
  }

  private showValidationError(message: string) {
    // Show error notification
    AppNotify.error(message);

    // Trigger error animation
    this.dropZoneValidationState = 'error';

    // Reset validation state after animation completes
    setTimeout(() => {
      this.dropZoneValidationState = 'default';
    }, 500);
  }

  private resetUpload() {
    this.textVisible = true;
    this.progressVisible = false;
    this.progressValue = 0;
    this.uploadedFile = undefined;
    this.uploadedFileSize = undefined;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  removeFile(event?: MouseEvent) {
    // CRITICAL: Stop event propagation to prevent dropzone from capturing the click
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.uploadedFileSize = undefined;
    this.uploadedFile = undefined;
    this.textVisible = true;
    this.progressVisible = false;
    this.progressValue = 0;

    // Reset the file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    this.onFileChanged.emit(undefined);
  }
}
